<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

import { PRESENTER_VALUE_FALLBACK } from '@satellite/components/elements/presenters/presenterValueFallback';
import CompanyPresenterV2 from '@satellite/components/elements/presenters/CompanyPresenterV2.vue';
import PresenterValueFallback from '@satellite/components/elements/presenters/PresenterValueFallback.vue';

import type { PresenterDisplaySize } from '@satellite/composables/presenterDisplayOptions';
import type {
  AppointmentDisplayField,
  AppointmentDisplayData
} from '@satellite/composables/useAppointmentDisplay';

const {
  data,
  size,
  orientation = 'vertical',
  displayFields = ['confirmationNumber']
} = defineProps<{
  data: AppointmentDisplayData;
  size?: PresenterDisplaySize;
  orientation?: 'horizontal' | 'vertical';
  displayFields?: AppointmentDisplayField[];
}>();

const resolvedSize = computed(() => size ?? data.size);

const { t } = useI18n();

const showConfirmationNumber = computed(() => displayFields.includes('confirmationNumber'));
const showRefNumbers = computed(() => displayFields.includes('refNumbers'));
const showStatus = computed(() => displayFields.includes('status'));
const showStatusChip = computed(() => displayFields.includes('statusChip'));
const showTimeInterval = computed(() => displayFields.includes('timeInterval'));
const showDate = computed(() => displayFields.includes('date'));
const showDateLong = computed(() => displayFields.includes('dateLong'));
const showStart = computed(() => displayFields.includes('start'));
const showEnd = computed(() => displayFields.includes('end'));
const showTimezone = computed(() => displayFields.includes('timezone'));
const showCarrierCompany = computed(() => displayFields.includes('carrierCompany'));
</script>

<template>
  <span
    class="d-flex appointment-display-v2"
    v-bind="$attrs"
    :class="{
      'flex-row align-center gap-1': orientation === 'horizontal',
      'flex-column align-start gap-0-5 justify-start': orientation === 'vertical',
      'typography-body-sm': resolvedSize === 'small',
      'typography-body-md': resolvedSize === 'regular',
      'typography-heading-lg-bold': resolvedSize === 'large'
    }">
    <template v-if="showConfirmationNumber">
      <slot
        name="appointment-confirmation-number"
        :confirmation-number="data.confirmationNumber"
        :confirmation-number-attrs="data.confirmationNumberAttrs">
        <presenter-value-fallback v-if="!data.confirmationNumber" />
        <span v-else v-bind="data.confirmationNumberAttrs">
          {{ data.confirmationNumber }}
        </span>
      </slot>
    </template>

    <template v-if="showRefNumbers">
      <slot
        name="appointment-ref-numbers"
        :ref-numbers="data.refNumbers"
        :ref-numbers-attrs="data.refNumbersAttrs">
        <presenter-value-fallback v-if="!data.refNumbers" />
        <span v-else v-bind="data.refNumbersAttrs">
          {{ data.refNumbers }}
        </span>
      </slot>
    </template>

    <template v-if="showDate">
      <slot name="appointment-date" :date="data.date" :date-attrs="data.dateAttrs">
        <presenter-value-fallback v-if="!data.date" />
        <span v-else v-bind="data.dateAttrs">
          {{ data.dateFormatted ?? PRESENTER_VALUE_FALLBACK }}
        </span>
      </slot>
    </template>

    <template v-if="showDateLong">
      <slot name="appointment-date-long" :date="data.date" :date-long-attrs="data.dateLongAttrs">
        <presenter-value-fallback v-if="!data.date" />
        <span v-else v-bind="data.dateLongAttrs">
          {{ data.dateLongFormatted ?? PRESENTER_VALUE_FALLBACK }}
        </span>
      </slot>
    </template>

    <template v-if="showStart">
      <slot name="appointment-start" :start="data.start" :start-attrs="data.startAttrs">
        <presenter-value-fallback v-if="!data.start" />
        <span v-else v-bind="data.startAttrs">
          {{ data.startFormatted ?? PRESENTER_VALUE_FALLBACK }}
        </span>
      </slot>
    </template>

    <template v-if="showEnd">
      <slot name="appointment-end" :end="data.end" :end-attrs="data.endAttrs">
        <presenter-value-fallback v-if="!data.end" />
        <span v-else v-bind="data.endAttrs">
          {{ data.endFormatted ?? PRESENTER_VALUE_FALLBACK }}
        </span>
      </slot>
    </template>

    <template v-if="showTimeInterval">
      <slot
        name="appointment-time-interval"
        :time-interval="{ start: data.start, end: data.end }"
        :time-interval-attrs="data.timeIntervalAttrs">
        <presenter-value-fallback
          v-if="!data.start || !data.end || !data.startFormatted || !data.endFormatted" />
        <span v-else v-bind="data.timeIntervalAttrs">
          {{ data.startFormatted }} - {{ data.endFormatted }}
        </span>
      </slot>
    </template>

    <template v-if="showTimezone">
      <slot
        name="appointment-timezone"
        :timezone="data.timezoneFormatted"
        :timezone-attrs="data.timezoneAttrs">
        <presenter-value-fallback v-if="!data.timezoneFormatted" />
        <span v-else v-bind="data.timezoneAttrs">
          {{ data.timezoneFormatted }}
        </span>
      </slot>
    </template>

    <template v-if="showStatus">
      <slot name="appointment-status" :status="data.status" :status-attrs="data.statusAttrs">
        <presenter-value-fallback v-if="!data.status" />
        <span v-else v-bind="data.statusAttrs">
          {{ data.statusLabel }}
        </span>
      </slot>
    </template>

    <template v-if="showStatusChip">
      <v-chip v-if="data.status" v-bind="data.statusChipAttrs({ size: resolvedSize })">
        <slot
          name="appointment-status-chip"
          :status="data.status"
          :translated-status="data.statusLabel"
          :status-chip-text-attrs="data.statusChipTextAttrs({ size: resolvedSize })">
          <span v-bind="data.statusChipTextAttrs({ size: resolvedSize })">
            {{ data.statusLabel }}
          </span>
        </slot>
      </v-chip>
      <presenter-value-fallback v-else />
    </template>

    <template v-if="showCarrierCompany">
      <company-presenter-v2
        v-if="data.carrierId || data.carrierCompanyId"
        :carrier-id="data.carrierId"
        :company-id="data.carrierCompanyId"
        :display-fields="['name']"
        :mode="data.mode"
        :size="resolvedSize"
        v-bind="data.carrierCompanyPresenterProps">
        <template #company-name="{ companyName, companyNameAttrs }">
          <slot
            name="carrier-company"
            :carrier-company="companyName"
            :carrier-company-attrs="companyNameAttrs">
            <span v-bind="companyNameAttrs">
              {{ companyName }}
            </span>
          </slot>
        </template>
      </company-presenter-v2>
      <presenter-value-fallback v-else>
        <template #default>
          {{ t('core.yard.messages.noVisitFound') }}
        </template>
      </presenter-value-fallback>
    </template>
  </span>
</template>
